import type {
    ComponentStageData,
    ComponentStageHeuristicDefinition,
    ConversionDal,
    PluginContext,
    ReadOnlyExtensionAPI
} from '../../types'
import {getParent, isHeaderContainer, isHeaderFirstRowChildComponentSpecialCase, updateTargetLayout} from '../common'
import {getMeasurementsPointer, getSourceLayoutPointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import {deepClone} from '@wix/wix-immutable-proxy'
import type {OrderPluginAPI} from '../order/types'
import {getContentBoxKey, getFallbackRatio, isInRange} from './common'
import {LOGO_RATIOS} from './constants'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'
import {pointerUtils} from '@wix/document-manager-core'
import {VIEW_MODES} from '../../../../constants/constants'
import type {Pointer} from '@wix/document-services-types'

const maxContent = {type: 'maxContent'}
const fr = {type: 'fr', value: 1}

const updateParentColumnAtIndex = (
    conversionDal: ConversionDal,
    componentPointer: Pointer,
    columnIndex: number,
    columnValue: {type: string; value?: number}
): void => {
    const parentPointer = getParent(conversionDal, componentPointer)
    const {containerLayout: parentContainerLayout} =
        deepClone(conversionDal.get(getTargetLayoutPointer(parentPointer!))) ?? {}
    const {columns} = parentContainerLayout

    columns[columnIndex] = columnValue

    updateTargetLayout(
        {conversionDal, componentPointer: parentPointer!},
        {
            containerLayout: {
                ...parentContainerLayout,
                columns
            }
        }
    )
}

export const containerColumnsHeuristics: ComponentStageHeuristicDefinition[] = [
    {
        predicate: (
            {componentPointer, conversionDal, config, globalContext}: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionApi: ReadOnlyExtensionAPI
        ) => {
            if (
                !isHeaderFirstRowChildComponentSpecialCase(
                    componentPointer,
                    conversionDal,
                    globalContext,
                    readOnlyExtensionApi
                )
            )
                return false
            return !!readOnlyExtensionApi?.layout.isContentWidth(
                pointerUtils.getCompPointer(componentPointer.id, VIEW_MODES.DESKTOP, {variants: config.targetVariants})
            )
        },
        apply: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const {itemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
            const {gridArea} = itemLayout
            const {columnStart} = gridArea

            updateParentColumnAtIndex(conversionDal, componentPointer, columnStart - 1, maxContent)
        },
        heuristicCode: 'MAX CONTENT WIDTH IN HEADER FIRST ROW'
    },
    ...LOGO_RATIOS.map(({min, max, target}) => ({
        predicate: (
            componentStageData: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionApi: ReadOnlyExtensionAPI
        ) => {
            const {componentPointer, conversionDal, globalContext} = componentStageData

            if (
                !isHeaderFirstRowChildComponentSpecialCase(
                    componentPointer,
                    conversionDal,
                    globalContext,
                    readOnlyExtensionApi
                )
            )
                return false
            const component = conversionDal.get(componentPointer)
            const {componentLayout} = conversionDal.get(getSourceLayoutPointer(componentPointer))
            const ratio =
                componentLayout?.height?.type === 'aspectRatio'
                    ? componentLayout.height.value
                    : getFallbackRatio(componentStageData)
            return component.semanticType === SemanticTypes.LOGO && isInRange(ratio, min, max)
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, conversionDal} = componentStageData
            const parentPointer = getParent(conversionDal, componentPointer)

            const parentAvailableWidth = contextHelper.get(getContentBoxKey(parentPointer!))

            updateParentColumnAtIndex(conversionDal, componentPointer, 0, {
                type: 'percentage',
                value: (target / parentAvailableWidth) * 100
            })
        },
        heuristicCode: `FIRST ROW LOGO IN HEADER WITH ASPECT RATIO WIDTH ${target}`
    })),
    {
        predicate: (
            {componentPointer, conversionDal, globalContext}: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionApi: ReadOnlyExtensionAPI
        ) => {
            return isHeaderFirstRowChildComponentSpecialCase(
                componentPointer,
                conversionDal,
                globalContext,
                readOnlyExtensionApi
            )
        },
        apply: (componentStageData: ComponentStageData, contextHelper: PluginContext) => {
            const {componentPointer, conversionDal} = componentStageData

            const parentPointer = getParent(conversionDal, componentPointer)
            const {itemLayout} = conversionDal.get(getTargetLayoutPointer(componentPointer))
            const {gridArea} = itemLayout
            const {columnStart} = gridArea
            const {width} = conversionDal.get(getMeasurementsPointer(componentPointer))

            const parentAvailableWidth = contextHelper.get(getContentBoxKey(parentPointer!))

            updateParentColumnAtIndex(conversionDal, componentPointer, columnStart - 1, {
                type: 'percentage',
                value: (width / parentAvailableWidth) * 100
            })
        },
        heuristicCode: 'COMPONENT IN HEADER FIRST ROW SPECIAL CASE'
    },
    {
        predicate: (
            {componentPointer, conversionDal, globalContext}: ComponentStageData,
            _contextHelper: PluginContext,
            readOnlyExtensionAPI?: ReadOnlyExtensionAPI
        ) => {
            const {order} = readOnlyExtensionAPI!.getPluginsApi(globalContext)
            const orderApi = order as OrderPluginAPI
            return isHeaderContainer({componentPointer, conversionDal}) && orderApi.isHeaderSpecialCase()
        },
        apply: (
            componentStageData: ComponentStageData,
            contextHelper?: PluginContext,
            readOnlyExtensionAPI?: ReadOnlyExtensionAPI
        ) => {
            const {componentPointer, conversionDal, globalContext} = componentStageData
            const {order} = readOnlyExtensionAPI!.getPluginsApi(globalContext)
            const orderApi = order as OrderPluginAPI

            const columnCount = orderApi.getContainerColumnCount(componentPointer) ?? 1

            const columns = [...Array(columnCount)].map(() => fr)
            const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}

            updateTargetLayout(componentStageData, {
                containerLayout: {
                    ...containerLayout,
                    columns,
                    type: 'GridContainerLayout'
                }
            })
        },
        heuristicCode: 'HEADER CONTAINER SPECIAL CASE COLUMNS'
    },
    {
        predicate: ({componentPointer, conversionDal}: ComponentStageData) => {
            const component = conversionDal.get(componentPointer)
            return component.type === 'Container' && !component.isSlottedComponent
        },
        apply: (componentStageData: ComponentStageData) => {
            const {componentPointer, conversionDal} = componentStageData
            const {containerLayout} = deepClone(conversionDal.get(getTargetLayoutPointer(componentPointer))) ?? {}

            updateTargetLayout(componentStageData, {
                containerLayout: {
                    ...containerLayout,
                    columns: [
                        {
                            type: 'MinMaxSize',
                            min: {type: 'px', value: 0},
                            max: fr
                        }
                    ],
                    type: 'GridContainerLayout'
                }
            })
        },
        heuristicCode: 'SINGLE COLUMN LAYOUT'
    }
]
